// The modern shell's navigation model: which sidebar section each page lives in, and which pages a
// person is shown. Pure data plus pure functions, so it is unit-tested. It reads lib/nav.js (the one list
// of pages, routes and permissions), so a page added there appears in the sidebar without being listed
// here: an id with no entry falls back to the section of its nav.js `app` group. Visibility is the SAME
// rule the other shells use (role / tenant grant via `canAccess`, admin-only, landing-price-only,
// reversible module flags); hiding a link is never the access control, every API route checks again.
import { NAV } from "@/lib/nav";
import {
  SHOW_DELIVERY, SHOW_INVENTORY, SHOW_FINANCE, SHOW_REPORTS, SHOW_RECORD_CHAIN, SHOW_INCENTIVES,
} from "@/lib/featureFlags";

export const ITEM_FLAG = {
  projects: SHOW_DELIVERY, dwsurvey: SHOW_DELIVERY, outstanding: SHOW_DELIVERY, service: SHOW_DELIVERY,
  inventory: SHOW_INVENTORY, "stock-ledger": SHOW_INVENTORY, "purchase-orders": SHOW_INVENTORY, "goods-receipts": SHOW_INVENTORY, "stock-counts": SHOW_INVENTORY,
  "manufacturer-orders": SHOW_INVENTORY, "inv-analytics": SHOW_INVENTORY,
  "invoice-gen": SHOW_FINANCE, "accounts-home": SHOW_FINANCE, petty: SHOW_FINANCE, cashbook: SHOW_FINANCE, "project-pnl": SHOW_FINANCE,
  "finance-payments": SHOW_FINANCE, expenses: SHOW_FINANCE, pnl: SHOW_FINANCE,
  incentives: SHOW_INCENTIVES, reports: SHOW_REPORTS, executive: SHOW_REPORTS, "record-chain": SHOW_RECORD_CHAIN,
};

// Sections in display order, set from how the sales and accounts teams actually use the CRM (production request
// logs, Oct 2026: Home / follow-ups, leads, Store Walk stock search, quotations, tasks and the daily planner for sales;
// outstanding, tax invoices, payments, money requests and cashbooks for accounts). "My Day" comes first for everyone,
// then selling, then orders and delivery, then the accounts desk; occasional tools (catalogues, stock, reports, people,
// setup) come after. A person only sees the sections they can open, so an accounts login gets My Day → Accounts.
// `ids` fixes the order inside a section (most used first). Every page is in exactly one section.
export const SECTIONS = [
  { id: "today", label: "My Day", ids: ["dashboard", "daily-planner", "start-here"] },
  { id: "sell", label: "Sales", ids: ["leads", "store-walk", "quotes", "quote-followups", "visitors", "calls", "customers",
    "architects", "pipeline", "quote-builder"] },
  { id: "orders", label: "Orders & Delivery", ids: ["sales", "delivery-challans", "projects", "dwsurvey", "service",
    "manufacturer-orders"] },
  { id: "accounts", label: "Accounts", ids: ["accounts-home", "outstanding", "invoice-gen", "finance-payments", "expenses", "cashbook", "pnl",
    "project-pnl", "incentives", "payroll", "petty"] },
  { id: "catalogue", label: "Catalogues & Studio", ids: ["catalogues", "virtual-catalogue", "architect-studio", "vendor-brochures"] },
  { id: "stock", label: "Stock & Purchasing", ids: ["inventory", "stock-ledger", "purchase-orders", "goods-receipts", "stock-counts", "inv-analytics"] },
  { id: "insights", label: "Reports", ids: ["sales-tracker", "analytics", "reports", "executive", "record-chain"] },
  { id: "people", label: "People & Admin", ids: ["attendance", "discussions", "roles", "audit-trail"] },
  { id: "setup", label: "Setup", ids: ["forms", "workflows", "flows", "data-centre", "go-live", "data-check"] },
];

// Sections folded until the person opens them (their choice is remembered): used weekly, not daily.
export const FOLDED_BY_DEFAULT = ["insights", "people", "setup"];

// Pages that still exist and still route, but aren't their own sidebar entry. Daily Planner, Tasks, Meet
// Planner, Lead Follow-ups and Work Scheduler are one workflow — planning the day, for yourself or (Work
// Scheduler) for the team — so a separate sidebar row for each felt like five clicks for one job. Alerts
// folds all the way in as a Daily Planner panel; the other four stay full pages (Tasks and Meet Planner are
// full create/edit/manage screens, not just a "due today" view) reached from Daily Planner's PlannerTabs
// strip instead of their own sidebar row. Not a permission check — someone who opens the link, or has one
// as their saved landing page, still reaches the page itself.
export const HIDDEN_FROM_SIDEBAR = ["alerts", "tasks", "meetplan", "followups", "work-schedule"];

// The phone / tablet bottom bar: the first four pages here that the person can open, most used first. A sales login
// gets Home, Leads, Store Walk, Quotes; an accounts login Home, Outstanding, Invoices, Payments.
export const QUICK_IDS = ["dashboard", "leads", "store-walk", "quotes", "outstanding", "invoice-gen", "finance-payments",
  "expenses", "tasks", "projects", "daily-planner"];
export const QUICK_LABELS = {
  dashboard: "Home", "store-walk": "Store walk", "invoice-gen": "Invoices", "finance-payments": "Payments",
  expenses: "Requests", "daily-planner": "My day", quotes: "Quotes",
};

/** Up to `n` bottom-bar pages for this person, in QUICK_IDS order. */
export function quickItems(isVisible, n = 4, nav = NAV) {
  const byId = Object.fromEntries(nav.map((i) => [i.id, i]));
  return QUICK_IDS.map((id) => byId[id]).filter((i) => i && isVisible(i)).slice(0, n)
    .map((i) => ({ ...i, short: QUICK_LABELS[i.id] || i.label }));
}

// nav.js `app` group -> section, for a page that is not listed above.
const APP_SECTION = { overview: "today", sell: "sell", deliver: "orders", stock: "stock", money: "accounts",
  relations: "sell", command: "setup" };

const BY_ID = {};
SECTIONS.forEach((s) => s.ids.forEach((id) => { BY_ID[id] = s.id; }));

export const sectionOf = (item) => (item ? BY_ID[item.id] || APP_SECTION[item.app] || "setup" : "setup");
export const sectionLabel = (id) => (SECTIONS.find((s) => s.id === id) || {}).label || "";

/** The pages a person may open, grouped: [{id, label, items}]; empty sections are left out. */
export function buildSections(isVisible, nav = NAV) {
  const byId = Object.fromEntries(nav.map((n) => [n.id, n]));
  const placed = new Set(HIDDEN_FROM_SIDEBAR);
  const out = SECTIONS.map((s) => {
    const items = s.ids.map((id) => byId[id]).filter((i) => i && isVisible(i));
    items.forEach((i) => placed.add(i.id));
    return { id: s.id, label: s.label, items };
  });
  // Anything in nav.js that no section lists still shows, in its app group's section.
  nav.filter((i) => !placed.has(i.id) && isVisible(i)).forEach((i) => {
    out.find((s) => s.id === sectionOf(i)).items.push(i);
  });
  return out.filter((s) => s.items.length);
}

/** The visibility rule shared with the other shells. */
export function makeVisible({ user, canAccess, canSeeCost }) {
  return (item) => {
    if (item.id in ITEM_FLAG && !ITEM_FLAG[item.id]) return false;
    if (item.adminOnly && user?.role !== "admin") return false;
    if (item.costOnly && !canSeeCost) return false;
    return Boolean(canAccess(item.perm || item.id));
  };
}
